import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { fmtPrice, fmtMktCap } from '../lib/colors';

const MARGIN = { top: 24, right: 20, bottom: 44, left: 68 };
const CHART_LINE_COLOR = '#2962ff';
const AREA_COLOR = 'rgba(41, 98, 255, 0.10)';
const GRID_COLOR = 'rgba(42, 49, 80, 0.8)';
const AXIS_COLOR = '#4a5468';
const LABEL_COLOR = '#787b86';
const TOOLTIP_LINE_COLOR = 'rgba(209, 212, 220, 0.35)';

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateShort(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

function formatValue(v) {
  if (v >= 1e6)  return '$' + (v / 1e6).toFixed(2) + 'M';
  if (v >= 1e3)  return '$' + (v / 1e3).toFixed(1) + 'K';
  return '$' + v.toFixed(0);
}

function niceRange(min, max) {
  const range = max - min;
  const step = Math.pow(10, Math.floor(Math.log10(range))) * (range / 5 > 5 * Math.pow(10, Math.floor(Math.log10(range))) ? 5 : 1);
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = lo; v <= hi; v += step) ticks.push(parseFloat(v.toFixed(10)));
  return { lo, hi, ticks };
}

export default function BacktestChart({ portfolioData, holdings }) {
  // portfolioData: [{ date: 'YYYY-MM-DD', value: number, positions: { TICKER: number } }]
  // holdings: [{ ticker, shares, purchaseDate }]

  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [hoverX, setHoverX] = useState(null); // svg x coordinate
  const [hoverIdx, setHoverIdx] = useState(null);
  const [dragStartX, setDragStartX] = useState(null);
  const [dragCurrentX, setDragCurrentX] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setSize({ width: Math.floor(width), height: Math.floor(height) });
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const { width, height } = size;
  const cw = width - MARGIN.left - MARGIN.right;
  const ch = height - MARGIN.top - MARGIN.bottom;

  const { minVal, maxVal, xTicks, yInfo, points } = useMemo(() => {
    if (!portfolioData?.length || cw < 10 || ch < 10) {
      return { minVal: 0, maxVal: 1, xTicks: [], yInfo: { ticks: [] }, points: [] };
    }

    const values = portfolioData.map(d => d.value);
    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);
    const pad = (rawMax - rawMin) * 0.05 || rawMax * 0.05 || 100;

    const yInfo = niceRange(rawMin - pad, rawMax + pad);
    const dates = portfolioData.map(d => new Date(d.date + 'T00:00:00').getTime());
    const minDate = Math.min(...dates);
    const maxDate = Math.max(...dates);
    const dateRange = maxDate - minDate || 1;

    const xScale = (ts) => ((ts - minDate) / dateRange) * cw;
    const yScale = (v) => ch - ((v - yInfo.lo) / (yInfo.hi - yInfo.lo)) * ch;

    const points = portfolioData.map((d, i) => {
      const ts = dates[i];
      return { x: xScale(ts), y: yScale(d.value), ...d };
    });

    // X axis ticks: ~6
    const targetTicks = Math.min(6, portfolioData.length);
    const step = Math.floor(portfolioData.length / (targetTicks - 1)) || 1;
    const xTicks = portfolioData
      .filter((_, i) => i % step === 0 || i === portfolioData.length - 1)
      .map((d, _) => {
        const ts = new Date(d.date + 'T00:00:00').getTime();
        return { x: xScale(ts), label: formatDateShort(d.date) };
      });

    return { minVal: yInfo.lo, maxVal: yInfo.hi, xTicks, yInfo, points };
  }, [portfolioData, cw, ch]);

  const pathD = useMemo(() => {
    if (!points.length) return '';
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  }, [points]);

  const areaD = useMemo(() => {
    if (!points.length) return '';
    return `${pathD} L ${points[points.length - 1].x.toFixed(1)} ${ch} L ${points[0].x.toFixed(1)} ${ch} Z`;
  }, [pathD, points, ch]);

  // Find closest point to hoverX
  useEffect(() => {
    if (hoverX === null || !points.length) {
      setHoverIdx(null);
      return;
    }
    let closest = 0;
    let minDist = Infinity;
    points.forEach((p, i) => {
      const d = Math.abs(p.x - hoverX);
      if (d < minDist) { minDist = d; closest = i; }
    });
    setHoverIdx(closest);
  }, [hoverX, points]);

  const handleMouseMove = useCallback((e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left - MARGIN.left;
    setHoverX(x);
    if (isDragging) setDragCurrentX(x);
  }, [isDragging]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left - MARGIN.left;
    setIsDragging(true);
    setDragStartX(x);
    setDragCurrentX(x);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (!isDragging) {
      setHoverX(null);
      setHoverIdx(null);
    }
  }, [isDragging]);

  useEffect(() => {
    function handleWindowMouseUp() {
      setIsDragging(false);
    }
    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => window.removeEventListener('mouseup', handleWindowMouseUp);
  }, []);

  const hoverPoint = hoverIdx != null ? points[hoverIdx] : null;
  const hoverData  = hoverIdx != null ? portfolioData[hoverIdx] : null;
  const hasSelection =
    dragStartX != null && dragCurrentX != null && Math.abs(dragCurrentX - dragStartX) > 6;

  const selectedRange = useMemo(() => {
    if (!hasSelection || !points.length) return null;
    const x0 = Math.min(dragStartX, dragCurrentX);
    const x1 = Math.max(dragStartX, dragCurrentX);

    let startIdx = 0;
    let endIdx = points.length - 1;
    let minStart = Infinity;
    let minEnd = Infinity;
    points.forEach((p, i) => {
      const ds = Math.abs(p.x - x0);
      const de = Math.abs(p.x - x1);
      if (ds < minStart) {
        minStart = ds;
        startIdx = i;
      }
      if (de < minEnd) {
        minEnd = de;
        endIdx = i;
      }
    });
    if (startIdx > endIdx) [startIdx, endIdx] = [endIdx, startIdx];
    return { x0, x1, startIdx, endIdx };
  }, [dragCurrentX, dragStartX, hasSelection, points]);

  const selectedSummary = useMemo(() => {
    if (!selectedRange || !portfolioData.length) return null;
    const start = portfolioData[selectedRange.startIdx];
    const end = portfolioData[selectedRange.endIdx];
    if (!start || !end || !start.value) return null;
    const pnl = end.value - start.value;
    return {
      start,
      end,
      pnl,
      pct: (pnl / start.value) * 100,
    };
  }, [portfolioData, selectedRange]);

  if (!portfolioData?.length) {
    return (
      <div className="stonks-empty" style={{ flex: 1 }}>
        <div className="stonks-empty-icon">📈</div>
        <div>No portfolio data to chart</div>
      </div>
    );
  }

  // First and last for P&L display
  const startVal = portfolioData[0]?.value || 0;
  const endVal   = portfolioData[portfolioData.length - 1]?.value || 0;
  const totalPnl = endVal - startVal;
  const totalPct = startVal ? (totalPnl / startVal) * 100 : 0;
  const isUp = totalPnl >= 0;

  return (
    <div className="stonks-chart-wrap" ref={containerRef}>
      {/* P&L summary bar */}
      <div style={{
        position: 'absolute', top: 0, left: MARGIN.left, right: MARGIN.right,
        display: 'flex', gap: 20, alignItems: 'center', fontSize: 12, zIndex: 5,
      }}>
        <span style={{ color: 'var(--s-text-dim)' }}>Portfolio Value</span>
        <span style={{ fontWeight: 700, fontSize: 15, fontFamily: 'var(--s-mono)', color: 'var(--s-text)' }}>
          {formatValue(endVal)}
        </span>
        <span style={{
          fontWeight: 600, fontFamily: 'var(--s-mono)',
          color: isUp ? 'var(--s-green)' : 'var(--s-red)',
        }}>
          {isUp ? '+' : ''}{formatValue(totalPnl)} ({totalPct >= 0 ? '+' : ''}{totalPct.toFixed(2)}%)
        </span>
        {selectedSummary && (
          <span style={{
            fontWeight: 600,
            fontFamily: 'var(--s-mono)',
            color: selectedSummary.pnl >= 0 ? 'var(--s-green)' : 'var(--s-red)',
          }}>
            Selected: {selectedSummary.pct >= 0 ? '+' : ''}{selectedSummary.pct.toFixed(2)}% ({formatDateShort(selectedSummary.start.date)} → {formatDateShort(selectedSummary.end.date)})
          </span>
        )}
      </div>

      {/* SVG chart */}
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ display: 'block', overflow: 'visible', userSelect: 'none', WebkitUserSelect: 'none' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <defs>
          <linearGradient id="chart-area-gradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={CHART_LINE_COLOR} stopOpacity="0.25" />
            <stop offset="100%" stopColor={CHART_LINE_COLOR} stopOpacity="0.02" />
          </linearGradient>
          <clipPath id="chart-clip">
            <rect x={0} y={0} width={cw} height={ch} />
          </clipPath>
        </defs>

        <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
          {/* Grid lines */}
          {yInfo.ticks.map((tick, i) => {
            const y = ch - ((tick - yInfo.lo) / (yInfo.hi - yInfo.lo)) * ch;
            return (
              <g key={tick}>
                <line x1={0} y1={y} x2={cw} y2={y} stroke={GRID_COLOR} strokeWidth={1} />
                <text x={-8} y={y} textAnchor="end" dominantBaseline="middle" fill={LABEL_COLOR} fontSize={10} fontFamily="'JetBrains Mono', monospace">
                  {formatValue(tick)}
                </text>
              </g>
            );
          })}

          {/* X axis ticks */}
          {xTicks.map(tick => (
            <g key={tick.label + tick.x}>
              <line x1={tick.x} y1={ch} x2={tick.x} y2={ch + 4} stroke={AXIS_COLOR} />
              <text x={tick.x} y={ch + 16} textAnchor="middle" fill={LABEL_COLOR} fontSize={10} fontFamily="'JetBrains Mono', monospace">
                {tick.label}
              </text>
            </g>
          ))}

          {/* X and Y axes */}
          <line x1={0} y1={0} x2={0} y2={ch} stroke={AXIS_COLOR} />
          <line x1={0} y1={ch} x2={cw} y2={ch} stroke={AXIS_COLOR} />

          {/* Area fill */}
          <path d={areaD} fill="url(#chart-area-gradient)" clipPath="url(#chart-clip)" />

          {/* Drag-selected window */}
          {selectedRange && (
            <rect
              x={selectedRange.x0}
              y={0}
              width={Math.max(0, selectedRange.x1 - selectedRange.x0)}
              height={ch}
              fill="rgba(41, 98, 255, 0.12)"
              stroke="rgba(41, 98, 255, 0.45)"
              strokeWidth={1}
            />
          )}

          {/* Price line */}
          <path d={pathD} fill="none" stroke={CHART_LINE_COLOR} strokeWidth={2} strokeLinejoin="round" clipPath="url(#chart-clip)" />

          {/* Hover crosshair + dot */}
          {hoverPoint && (
            <>
              <line
                x1={hoverPoint.x} y1={0}
                x2={hoverPoint.x} y2={ch}
                stroke={TOOLTIP_LINE_COLOR}
                strokeWidth={1}
                strokeDasharray="4 4"
              />
              <circle
                cx={hoverPoint.x}
                cy={hoverPoint.y}
                r={5}
                fill={CHART_LINE_COLOR}
                stroke="var(--s-bg)"
                strokeWidth={2}
              />
            </>
          )}
        </g>
      </svg>

      {/* Hover tooltip */}
      {hoverPoint && hoverData && (
        <div
          className="stonks-chart-tooltip"
          style={{
            left: (() => {
              const lx = hoverPoint.x + MARGIN.left + 14;
              return lx + 220 > width ? hoverPoint.x + MARGIN.left - 234 : lx;
            })(),
            top: MARGIN.top + Math.max(0, hoverPoint.y - 80),
            opacity: 1,
          }}
        >
          <div className="stonks-chart-tooltip-date">{formatDate(hoverData.date)}</div>
          <div className="stonks-chart-tooltip-total">{formatValue(hoverData.value)}</div>
          {hoverData.positions && Object.entries(hoverData.positions)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8)
            .map(([ticker, val]) => (
              <div className="stonks-chart-tooltip-row" key={ticker}>
                <span style={{ fontWeight: 600 }}>{ticker}</span>
                <span style={{ color: 'var(--s-text)' }}>{formatValue(val)}</span>
              </div>
            ))
          }
          {hoverData.positions && Object.keys(hoverData.positions).length > 8 && (
            <div style={{ fontSize: 10, color: 'var(--s-text-muted)', marginTop: 2 }}>
              +{Object.keys(hoverData.positions).length - 8} more...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
